import React, { PropsWithChildren, useState, useEffect } from 'react'
import { Layout, Checkbox, Tooltip, Input, notification, Select } from 'antd'
import AntdIcon, { LoadingOutlined, MenuOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import dayjs from 'dayjs'

import { LogoExiros } from '@icons'
import { NotificationEnum, RoleEnum, SVGIcon } from '@types'
import { useUser, useGeneral, useContractContracts } from '@contexts'
import { config } from '@config'
import { LOCAL_STORAGE_KEYS } from '@constants'
import { AuthApi, QUERY_KEYS } from '@api'

import '../../firebase/firebase'
import {
  ContainerButtons,
  LogoContainer,
  StyledButton,
  StyledHeader,
  StyledSider,
  MenuOverlay,
  UserColor,
  ContainerCheckbox,
  StyledCheckboxGroup,
  StyledContainer,
  JobUpdateContainer,
  SearchContainer,
} from './components'
import { useContractsUpdate } from '../ContractsDashboard/hooks/useContractsUpdate'
import { StyledFont } from '../StyledComponents'
import { contractUpdateApi } from '../ContractsDashboard/hooks/useContractsUpdate/api'
import UserInfo from '../UserInfo'
import WebSocketIndicator from '../WebSocketIndicator'

// TODO: delete when all dashboards are refactored
interface DashboardLayoutProps {
  buttons?: {
    name: string
    primary: boolean
    svgColor: string
    icon: typeof AntdIcon | SVGIcon
    action: () => void
  }[]
  checkboxesGroup?: {
    name: string
    checkboxes: string[]
  }[]
  menu?: () => JSX.Element
  searchSelectOptions: { label: string; value: string }[]
}

const messaging = getMessaging()
let firebaseTokenSaved = false

const DashboardLayout: React.FC<PropsWithChildren<DashboardLayoutProps>> = ({
  checkboxesGroup,
  buttons,
  children,
  menu,
  searchSelectOptions,
}) => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const queryClient = useQueryClient()
  const { setTicketToSearch, menuDisplayed, handleDisplayMenu } = useGeneral()
  const roleUser = user?.roles[0].name
  const roles = user?.roles.map(role => role.name)
  const { ADMIN, SUPPLIER, CARRIER, PROCUREMENT, ADMINRO, COMMERCIAL } = RoleEnum
  const { pathname } = useLocation()
  const { contractUpdate, isContractLoading, updateContracts } = useContractsUpdate()
  const { isContractsSync, setIsContractsSync } = useContractContracts()
  const [searchInputValue, setSearchInputValue] = useState<undefined | string>()
  const [filterSelectSelected, setFilterSelectSelected] = useState(searchSelectOptions[0].value)
  const notificationKey = 'loginNotificationShown'
  const hasNotificationBeenShown = () => {
    return localStorage.getItem(notificationKey) === 'true'
  }
  const markNotificationAsShown = () => {
    localStorage.setItem(notificationKey, 'true')
  }

  const handleUpdateContracts = async () => {
    setIsContractsSync(true)
    notification.info({
      message: t('globals.synchronizing_contracts'),
      key: 'synchronizingContracts',
    })
    try {
      await contractUpdateApi.syncContracts()
      queryClient.resetQueries([QUERY_KEYS.CONTRACTS])
      notification.destroy('synchronizingContracts')
      notification.success({
        message: t('globals.contracts_synchronized'),
      })
      updateContracts()
      setIsContractsSync(false)
    } catch {
      notification.error({
        message: t('globals.error_sync_contracts'),
      })
      setIsContractsSync(false)
    }
  }

  const contractUrl = pathname.includes('contract')
  const routesUrl = pathname.includes('routes')
  const settingsUrl = pathname.includes('settings')
  const simulationUrl = pathname.includes('simulation')
  const batcheesnUrl = pathname.includes('batchees')
  const carriersAdminViewUrl = pathname.includes('carriers')
  const vehiclesAdminViewUrl = pathname.includes('vehicles')
  const driversAdminViewUrl = pathname.includes('drivers')
  const trailersAdminViewUrl = pathname.includes('trailers')
  const openAdminViewUrl = pathname.includes('open')
  const closedAdminViewUrl = pathname.includes('closed')
  const certifiedAdminViewUrl = pathname.includes('certified')
  getToken(messaging, {
    vapidKey: config.vapidKey,
  })
    .then(firebaseToken => {
      if (user?.id && !firebaseTokenSaved) {
        AuthApi.saveFirebaseToken(user.id, firebaseToken)
        firebaseTokenSaved = true
        window.localStorage.setItem(LOCAL_STORAGE_KEYS.FIREBASE, firebaseToken)
      }
    })
    .catch(() => {
      if (!hasNotificationBeenShown()) {
        notification.success({
          message: t('globals.push_notifications'),
          duration: 3,
          key: 'notification',
        })

        markNotificationAsShown()
      }
    })
  onMessage(messaging, payload => {
    switch (payload.data?.type) {
      case NotificationEnum.NEW_TICKET_ADMIN:
        if (roles?.includes(ADMIN)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.supplierName} ha creado una nueva solicitud #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        if (roles?.includes(ADMINRO)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.supplierName} ha creado una nueva solicitud #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.DECLINED:
        if (roles?.includes(ADMIN)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.carrierName} ha cancelado el servicio #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        if (roles?.includes(ADMINRO)) {
          queryClient.refetchQueries([QUERY_KEYS.TO_AUTHORIZE, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `${payload.data?.carrierName} ha cancelado el servicio #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.NEW_TICKET_CARRIER:
        if (roles?.includes(CARRIER)) {
          queryClient.refetchQueries([QUERY_KEYS.PENDINGS, QUERY_KEYS.TICKETS])
          return notification.success({
            message: `Tiene una nueva solicitud #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.NEW_TICKET_SUPPLIER:
        if (roles?.includes(SUPPLIER) || roles?.includes(COMMERCIAL)) {
          queryClient.refetchQueries([QUERY_KEYS.TICKETS])
          return notification.success({
            message: `La administración creó una nueva solicitud #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.AUTHORIZED:
        if (roles?.includes(SUPPLIER) || roles?.includes(COMMERCIAL)) {
          queryClient.refetchQueries([QUERY_KEYS.TICKETS])
          return notification.success({
            message: `La administración aprobó la solicitud #${payload.data?.ticketNumber}`,
            duration: 0,
          })
        }
        return null
      case NotificationEnum.CONFIRMED:
        queryClient.refetchQueries([QUERY_KEYS.TICKETS])
        return notification.success({
          message: `Trip ID ${payload.data?.ticketNumber}: ${payload.data?.title} by ${payload.data?.carrierName}`,
          duration: 0,
        })
      case NotificationEnum.CANCELLED_REALLOCATED_TICKET:
        queryClient.refetchQueries([QUERY_KEYS.TICKETS])
        return notification.info({
          message: `Ticket desasignado. ID #${payload.data?.ticketNumber}`,
          duration: 0,
        })
      default:
        return notification.success({ message: 'Nueva información sin identificar' })
    }
  })

  const handleSearch = (value: string) => {
    setTicketToSearch(value)
  }

  const handleChangeSelect = (value: string) => {
    setSearchInputValue(undefined)
    setFilterSelectSelected(value)
  }

  const onLogout = () => {
    firebaseTokenSaved = false
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleDisplayMenu()
  }

  useEffect(() => {
    const isMobile = window.innerWidth <= 768
    if (isMobile && menuDisplayed) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuDisplayed])

  const [headerHeight, setHeaderHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const header = document.querySelector('.ant-layout-header')
    if (header) {
      setHeaderHeight(header.clientHeight)
    }
    const handleResize = () => {
      if (header) {
        setHeaderHeight(header.clientHeight)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleMenuContentScroll = (e: React.WheelEvent) => {
    e.stopPropagation()
  }

  return (
    <Layout style={{ height: '100vh' }}>
      <StyledHeader>
        <LogoContainer>
          <MenuOutlined className="displayer" onClick={handleMenuClick} />
          <LogoExiros />
        </LogoContainer>
        {roles?.includes(PROCUREMENT) ||
        contractUrl ||
        settingsUrl ||
        routesUrl ||
        batcheesnUrl ||
        carriersAdminViewUrl ||
        driversAdminViewUrl ||
        vehiclesAdminViewUrl ||
        trailersAdminViewUrl ||
        openAdminViewUrl ||
        closedAdminViewUrl ||
        certifiedAdminViewUrl ||
        simulationUrl ? null : (
          <SearchContainer>
            <Select
              value={filterSelectSelected}
              onChange={handleChangeSelect}
              className="search-type"
            >
              {searchSelectOptions.map(({ label, value }) => (
                <Select.Option key={label + value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
            <Input.Search
              className="ticket-search-input"
              placeholder="Search"
              type="string"
              onSearch={handleSearch}
              value={searchInputValue}
              onChange={({ target }) => setSearchInputValue(target.value)}
              allowClear
            />
          </SearchContainer>
        )}
        {contractUrl && (
          <JobUpdateContainer>
            <p>{t('admin_router.admin_dashboard.contracts.job_update')}</p>
            <StyledFont>
              <p>{dayjs(contractUpdate?.createAt).format('DD/MM/YYYY HH:mm')}</p>
            </StyledFont>
            <Tooltip
              title={
                isContractsSync || isContractLoading
                  ? t('globals.synchronizing_contracts')
                  : t('globals.tooltips.sync_contracts')
              }
            >
              {isContractsSync || isContractLoading ? (
                <LoadingOutlined className="load-icon" />
              ) : (
                <ReloadOutlined className="load-icon" onClick={handleUpdateContracts} />
              )}
            </Tooltip>
          </JobUpdateContainer>
        )}
        <div className="websocket-user-container">
          <WebSocketIndicator />
          <div className="header-user-info">
            <UserInfo onLogout={onLogout} />
          </div>
        </div>
      </StyledHeader>
      <UserColor role={roleUser} />
      <Layout style={{ position: 'relative' }}>
        <MenuOverlay
          className={menuDisplayed ? 'visible' : ''}
          onClick={menuDisplayed ? handleDisplayMenu : undefined}
        />
        <StyledSider
          collapsed={!menuDisplayed}
          collapsedWidth={0}
          className={menuDisplayed ? 'menu-displayed' : undefined}
          style={
            menuDisplayed && isMobile && headerHeight > 0
              ? { top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }
              : undefined
          }
        >
          <div className="menu-content" onWheel={handleMenuContentScroll}>
            {menu && menu()}
            <>
              {buttons && (
                <ContainerButtons>
                  {buttons.map(button => (
                    <StyledButton
                      type={button.primary ? 'primary' : 'default'}
                      svgColor={button.svgColor}
                      block
                      onClick={button.action}
                      icon={<button.icon />}
                    >
                      {button.name}
                    </StyledButton>
                  ))}
                </ContainerButtons>
              )}
              {checkboxesGroup?.map(({ name, checkboxes }) => (
                <StyledCheckboxGroup>
                  <p>{name}</p>
                  {checkboxes.map(checkbox => (
                    <ContainerCheckbox span={24}>
                      <Checkbox value={checkbox}>{checkbox}</Checkbox>
                    </ContainerCheckbox>
                  ))}
                </StyledCheckboxGroup>
              ))}
            </>
          </div>
          <div className="menu-user-info">
            <UserInfo onLogout={onLogout} />
          </div>
        </StyledSider>
        <StyledContainer>{children}</StyledContainer>
      </Layout>
    </Layout>
  )
}

export { DashboardLayout }
